

import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { api } from "../../App";
import { decryptResponse } from "../CryptoUtil";

const RECORDS_PER_PAGE = 10;

const parseJsonValue = (value) => {
  if (value === null || value === undefined || value === "") return value;
  if (typeof value !== "string") return value;

  const trimmed = value.trim();
  if (!trimmed) return "";

  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const parseResponseBody = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

/** Supports the encrypted response formats used by the existing PHP APIs. */
const decodeApiResponse = (raw) => {
  const body = parseResponseBody(raw);
  if (!body || typeof body !== "object") return body;

  const envelope =
    body.encrypted && typeof body.encrypted === "object"
      ? body.encrypted
      : body;

  if (envelope.payload !== undefined && envelope.transactionId) {
    try {
      const decoded = decryptResponse(
        envelope.payload,
        envelope.transactionId
      );
      return parseResponseBody(decoded);
    } catch (error) {
      console.error("SM API response decryption failed:", error);
      // Return plain API response if this endpoint did not encrypt the body.
    }
  }

  return body;
};

const getSession = () => {
  try {
    return JSON.parse(localStorage.getItem("userSession") || "null");
  } catch {
    return null;
  }
};

const getToken = () => {
  const session = getSession();
  return (
    session?.token ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    ""
  );
};

const getRole = () => {
  const session = getSession();
  return String(
    session?.role ||
      localStorage.getItem("role") ||
      localStorage.getItem("userRole") ||
      ""
  )
    .trim()
    .toUpperCase();
};

const getRecordId = (record) =>
  String(
    record?.sm_code ??
      record?.sm_id ??
      record?.id ??
      record?.code ??
      record?.user_code ??
      ""
  ).trim();

const getText = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const getWorkingAreaText = (record) => {
  const source =
    record?.working_area ??
    record?.working_area_new ??
    record?.working_states ??
    record?.working_districts ??
    record?.working_cities;

  const parsed = parseJsonValue(source);
  if (parsed === null || parsed === undefined || parsed === "") return "-";

  if (Array.isArray(parsed)) {
    return parsed
      .map((item) => {
        if (typeof item === "string" || typeof item === "number") {
          return String(item);
        }
        if (item && typeof item === "object") {
          const state = item.state ?? item.name ?? item.state_name;
          const districts = item.districts ?? item.district ?? item.cities;
          if (state && districts !== undefined) {
            return `${state}: ${formatNestedArea(districts)}`;
          }
          return JSON.stringify(item);
        }
        return "";
      })
      .filter(Boolean)
      .join("\n");
  }

  if (parsed && typeof parsed === "object") return formatNestedArea(parsed);

  return String(parsed)
    .split(/[,;\n]+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .join("\n");
};

const formatNestedArea = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        item && typeof item === "object"
          ? formatNestedArea(item)
          : String(item ?? "").trim()
      )
      .filter(Boolean)
      .join(", ");
  }

  if (value && typeof value === "object") {
    return Object.entries(value)
      .map(([key, child]) => {
        if (Array.isArray(child) || (child && typeof child === "object")) {
          return `${key}: ${formatNestedArea(child)}`;
        }
        return `${key}: ${String(child ?? "")}`;
      })
      .join("\n");
  }

  return String(value ?? "");
};

const isInactive = (status) =>
  ["inactive", "disabled", "deleted", "blocked", "suspended", "0", "false"].includes(
    String(status ?? "active").trim().toLowerCase()
  );

const getApiErrorMessage = (error) => {
  const raw = error?.response?.data;
  if (raw) {
    const decoded = decodeApiResponse(raw);
    if (decoded?.message) return decoded.message;
    if (decoded?.error) return decoded.error;
    if (typeof decoded === "string" && decoded.trim()) return decoded;
  }

  if (error?.response?.status === 401) {
    return "Your login session is missing or expired. Please log in again.";
  }
  if (error?.response?.status === 403) {
    return "Your account is not authorized to view these SM records.";
  }
  if (error?.response?.status === 404) {
    return "getSm.php was not found. Please check the API URL and filename.";
  }
  if (error?.response?.status >= 500) {
    return "The server could not fetch SM records. Check the PHP error log.";
  }
  if (error?.code === "ECONNABORTED") {
    return "The request timed out. Please try again.";
  }
  if (!error?.response) {
    return "Cannot connect to the API. Check the server URL and network connection.";
  }
  return error?.message || "Unable to fetch SM records.";
};

const Sm_management = () => {
  const [smData, setSmData] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedSm, setSelectedSm] = useState(null);

  const fetchSmData = useCallback(async () => {
    if (!api) {
      setError("API URL is not configured in App.js.");
      setSmData([]);
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const token = getToken();
      const role = getRole();
      const endpoint = `${String(api).replace(/\/+$/, "")}/getSm.php`;

      const response = await axios.get(endpoint, {
        withCredentials: true,
        timeout: 20000,
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const result = decodeApiResponse(response.data);

      if (!result || typeof result !== "object") {
        throw new Error("Invalid response received from getSm.php.");
      }

      if (result.success === false || result.status === "error") {
        throw new Error(result.message || "Unable to fetch SM records.");
      }

      let records = Array.isArray(result.sms)
        ? result.sms
        : Array.isArray(result.data)
          ? result.data
          : Array.isArray(result.records)
            ? result.records
            : null;

      // Some API wrappers place the list inside result.data.data or result.data.sms.
      if (!records && result.data && typeof result.data === "object") {
        if (Array.isArray(result.data.sms)) records = result.data.sms;
        else if (Array.isArray(result.data.data)) records = result.data.data;
      }

      if (!Array.isArray(records)) {
        throw new Error(
          "The API response did not contain an SM list. Check the getSm.php response format."
        );
      }

      setSmData(records);
      setCurrentPage(1);
      setSuccess(`${records.length} SM record${records.length === 1 ? "" : "s"} loaded successfully.`);
      console.info("SM records loaded:", {
        count: records.length,
        role: result.role || role || "unknown",
      });
    } catch (requestError) {
      console.error("SM Management fetch failed:", {
        status: requestError?.response?.status,
        message: requestError?.message,
        response: requestError?.response?.data,
      });
      setError(getApiErrorMessage(requestError));
      setSmData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSmData();
  }, [fetchSmData]);

  const filteredData = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return smData;

    return smData.filter((sm) => {
      const searchable = [
        getRecordId(sm),
        sm?.sm_code,
        sm?.sm_id,
        sm?.id,
        sm?.zsm_id,
        sm?.zsm_code,
        sm?.asm_id,
        sm?.asm_code,
        sm?.admin_id,
        sm?.name,
        sm?.full_name,
        sm?.email,
        sm?.phone,
        sm?.state,
        sm?.city,
        sm?.status,
        getWorkingAreaText(sm),
      ];
      return searchable.some((value) =>
        String(value ?? "").toLowerCase().includes(term)
      );
    });
  }, [smData, search]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / RECORDS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * RECORDS_PER_PAGE;
  const currentRecords = filteredData.slice(startIndex, startIndex + RECORDS_PER_PAGE);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="row">
      <div className="col-12 grid-margin stretch-card">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
              <div>
                <h4 className="card-title mb-1">SM Management</h4>
                <p className="card-description mb-0">
                  View and search Sales Manager records assigned to your account.
                </p>
              </div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={fetchSmData}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Loading...
                  </>
                ) : (
                  <>
                    <i className="mdi mdi-refresh me-1" aria-hidden="true" />
                    Refresh
                  </>
                )}
              </button>
            </div>

            {error && (
              <div className="alert alert-danger d-flex justify-content-between align-items-start gap-2" role="alert">
                <div>
                  <i className="mdi mdi-alert-circle-outline me-2" aria-hidden="true" />
                  {error}
                </div>
                <button type="button" className="btn-close" aria-label="Dismiss error" onClick={() => setError("")} />
              </div>
            )}

            {success && !error && !loading && (
              <div className="alert alert-success d-flex justify-content-between align-items-center gap-2" role="status">
                <span>
                  <i className="mdi mdi-check-circle-outline me-2" aria-hidden="true" />
                  {success}
                </span>
                <button type="button" className="btn-close" aria-label="Dismiss message" onClick={() => setSuccess("")} />
              </div>
            )}

            <div className="row align-items-center g-3 mb-3">
              <div className="col-12 col-md-6 col-lg-4">
                <div className="input-group">
                  <span className="input-group-text" aria-hidden="true">
                    <i className="mdi mdi-magnify" />
                  </span>
                  <input
                    type="search"
                    className="form-control"
                    placeholder="Search by SM ID, name, phone, area..."
                    aria-label="Search SM records"
                    value={search}
                    onChange={handleSearchChange}
                  />
                  {search && (
                    <button type="button" className="btn btn-outline-secondary" onClick={() => { setSearch(""); setCurrentPage(1); }}>
                      Clear
                    </button>
                  )}
                </div>
              </div>
              <div className="col-12 col-md-6 col-lg-8 text-md-end">
                <span className="badge bg-primary-subtle text-primary me-2">
                  Total: {smData.length}
                </span>
                <span className="badge bg-secondary-subtle text-secondary">
                  Matching: {filteredData.length}
                </span>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th scope="col">S.No.</th>
                    <th scope="col">SM ID</th>
                    <th scope="col">ZSM ID</th>
                    <th scope="col">ASM ID</th>
                    <th scope="col">Admin ID</th>
                    <th scope="col">Name</th>
                    <th scope="col">Phone</th>
                    <th scope="col">State</th>
                    <th scope="col">Working Area</th>
                    <th scope="col">Status</th>
                    <th scope="col" className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={11} className="text-center py-5">
                        <span className="spinner-border spinner-border-sm text-primary me-2" role="status" aria-hidden="true" />
                        Fetching SM records...
                      </td>
                    </tr>
                  ) : currentRecords.length > 0 ? (
                    currentRecords.map((sm, index) => {
                      const status = sm?.status ?? "active";
                      const inactive = isInactive(status);
                      const area = getWorkingAreaText(sm);
                      return (
                        <tr key={`${getRecordId(sm) || "sm"}-${startIndex + index}`}>
                          <td>{startIndex + index + 1}</td>
                          <td className="fw-semibold">{getRecordId(sm) || "-"}</td>
                          <td>{getText(sm?.zsm_code ?? sm?.zsm_id)}</td>
                          <td>{getText(sm?.asm_code ?? sm?.asm_id)}</td>
                          <td>{getText(sm?.admin_code ?? sm?.admin_id)}</td>
                          <td>
                            <div className="fw-semibold">{getText(sm?.name ?? sm?.full_name)}</div>
                            {sm?.email && <small className="text-muted">{sm.email}</small>}
                          </td>
                          <td>{getText(sm?.phone)}</td>
                          <td>{getText(sm?.state)}</td>
                          <td>
                            <div className="text-wrap">
                              {area.split("\n").map((line, lineIndex) => (
                                <div key={`${lineIndex}-${line}`}>{line}</div>
                              ))}
                            </div>
                          </td>
                          <td>
                            <span className={`badge ${inactive ? "bg-danger-subtle text-danger" : "bg-success-subtle text-success"}`}>
                              {getText(status, "Active")}
                            </span>
                          </td>
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => setSelectedSm(sm)}
                              aria-label={`View details for ${getRecordId(sm) || sm?.name || "SM"}`}
                            >
                              <i className="mdi mdi-eye-outline me-1" aria-hidden="true" />
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={11} className="text-center py-5">
                        <i className="mdi mdi-account-search-outline d-block fs-2 text-muted mb-2" aria-hidden="true" />
                        <div className="fw-semibold">{error ? "SM records could not be loaded" : search ? "No matching SM records found" : "No SM records found"}</div>
                        <small className="text-muted">
                          {error ? "Check the API response or try refreshing." : search ? "Try a different search term." : "There are no records available for your account."}
                        </small>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mt-3">
              <small className="text-muted">
                {filteredData.length === 0
                  ? "Showing 0 records"
                  : `Showing ${startIndex + 1}–${Math.min(startIndex + RECORDS_PER_PAGE, filteredData.length)} of ${filteredData.length} records`}
              </small>
              <nav aria-label="SM records pagination">
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${safePage <= 1 || loading ? "disabled" : ""}`}>
                    <button type="button" className="page-link" disabled={safePage <= 1 || loading} onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}>
                      Previous
                    </button>
                  </li>
                  <li className="page-item disabled">
                    <span className="page-link">Page {safePage} of {totalPages}</span>
                  </li>
                  <li className={`page-item ${safePage >= totalPages || loading ? "disabled" : ""}`}>
                    <button type="button" className="page-link" disabled={safePage >= totalPages || loading} onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}>
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {selectedSm && (
        <div className="modal show d-block" tabIndex="-1" role="dialog" aria-modal="true" aria-labelledby="smDetailsTitle">
          <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title" id="smDetailsTitle">SM Details</h5>
                  <small className="text-muted">{getRecordId(selectedSm) || "Sales Manager"}</small>
                </div>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setSelectedSm(null)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  {[
                    ["SM ID", getRecordId(selectedSm)],
                    ["Name", selectedSm?.name ?? selectedSm?.full_name],
                    ["Email", selectedSm?.email],
                    ["Phone", selectedSm?.phone],
                    ["Admin ID", selectedSm?.admin_code ?? selectedSm?.admin_id],
                    ["ZSM ID", selectedSm?.zsm_code ?? selectedSm?.zsm_id],
                    ["ASM ID", selectedSm?.asm_code ?? selectedSm?.asm_id],
                    ["State", selectedSm?.state],
                    ["City", selectedSm?.city],
                    ["Address", selectedSm?.address],
                    ["Pincode", selectedSm?.pincode],
                    ["SM Type", selectedSm?.sm_type],
                    ["Status", selectedSm?.status],
                    ["Created At", selectedSm?.created_at],
                    ["Working Area", getWorkingAreaText(selectedSm)],
                  ].map(([label, value]) => (
                    <div className="col-12 col-md-6" key={label}>
                      <div className="border rounded p-3 h-100">
                        <small className="text-muted d-block mb-1">{label}</small>
                        <div className="text-break">
                          {label === "Working Area"
                            ? getText(value).split("\n").map((line, lineIndex) => (
                                <div key={`${lineIndex}-${line}`}>{line}</div>
                              ))
                            : getText(value)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setSelectedSm(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {selectedSm && <div className="modal-backdrop show" onClick={() => setSelectedSm(null)} />}
    </div>
  );
};

export default Sm_management;

